import { Combobox, ComboboxInput } from '@reach/combobox';
import { ListboxInput, ListboxButton, ListboxList } from '@reach/listbox';
import { useField } from 'formik';
import React__default, { useRef, useCallback } from 'react';
import styled from 'styled-components';
import { setTestId } from '../../lib/testId.js';
import { useQueryChildComponents } from '../base/SystemComponent.js';
import { Chip } from '../chip/Chip.js';
import { COLOR_PALETTE } from '../colors/colorPalette.js';
import { DEPRECATED_ComboboxList } from '../inputs/DEPRECATED_ComboboxCommon.js';
import { Stack } from '../layout/Stack.js';
import { useCheckModal } from '../modal/Modal.js';
import { CSSGenerateTextFieldError } from '../styles/cssGenerateField.js';
import { CSSDefaultInputField } from './common.js';
import { DEPRECATED_ComboboxOption, useSearchComboboxOptions } from './DEPRECATED_commonCombobox.js';
import { SelectPopover } from './DEPRECATED_commonSelect.js';
import { hasError } from './Field.js';
import { FIELD_ERROR_DATA_ATTRIBUTE, setErrorDataAttribute, FieldErrorMessage } from './FieldError.js';

const MIN_INPUT_WIDTH_REM = 13;
const StyledListboxInput = styled(ListboxInput)`
  width: 100%;
  min-width: ${MIN_INPUT_WIDTH_REM}rem;

  [${FIELD_ERROR_DATA_ATTRIBUTE}] > & [${Chip.dataId}],
  [${FIELD_ERROR_DATA_ATTRIBUTE}] > & [${Chip.dataId}]:hover,
  [${FIELD_ERROR_DATA_ATTRIBUTE}] > & [${Chip.dataId}]:focus {
    ${CSSGenerateTextFieldError()}
  }

  &[data-state="expanded"] [${Chip.dataId}-actions] {
    display: none;
  }
`;
const StyledCombobox = styled(Combobox)`
  position: relative;

  & [data-reach-combobox-list] {
    max-height: 14rem;
    overflow: auto;
  }
`;
const StyledComboboxInput = styled(ComboboxInput)`
  &[data-reach-combobox-input] {
    ${CSSDefaultInputField}
    min-height: 2.5rem;
    height: 2.5rem;
    padding: 0 1rem;
    justify-content: center;
    display: flex;
    font-size: 0.875rem;
    align-items: flex-start;
    width: 100%;
    min-width: ${MIN_INPUT_WIDTH_REM}rem;
    border: none;
    border-bottom: 1px solid ${COLOR_PALETTE.slateGrey40};
    border-radius: 0;
    background: none;
    color: inherit;
  }

  [${FIELD_ERROR_DATA_ATTRIBUTE}] > &,
    [${FIELD_ERROR_DATA_ATTRIBUTE}] > &:hover,
    [${FIELD_ERROR_DATA_ATTRIBUTE}] > &:focus {
    color: inherit;
  }

  &[data-reach-combobox-input][aria-expanded='true'],
  &[data-reach-combobox-input]:focus {
    outline: none;
  }
`;
/**
 * Select Input with Formik Field bindings.
 * Accepts `ComboboxOptions` as child components. If a 'deselected' option is required, it
 * must be manually added to options.
 *
 * @deprecated - use `SelectField` with an onSearch handler instead
 *
 * @example
 * <ChipComboboxField name="chicken">
 *  <ComboboxOption label="" value={SELECT_OPTION_NO_VALUE} />
 *  <ComboboxOption label="Egg" value="egg" />
 *  <ComboboxOption label="Duck" value="duck" />
 * </ChipComboboxField>
 */ const DEPRECATED_ChipComboboxField = ({ name , icon , label , isHighlighted , children , onFieldChange , placeholder , testId ="X" , reserveMessageSpace =true , ...props })=>{
    const isModal = useCheckModal();
    const [field, meta, helpers] = useField(name);
    const { setValue , setTouched  } = helpers;
    const [optionChildren, buttonChildren] = useQueryChildComponents(children, [
        DEPRECATED_ComboboxOption
    ]);
    const comboboxInputRef = useRef();
    const chipButtonRef = useRef();
    const { filteredChildren , handleChange , handleSearchTermChange , handleBlur , searchTotalCount , searchTerm , selectedLabel , setSearchTerm  } = useSearchComboboxOptions(optionChildren, field, setValue, setTouched, onFieldChange, true);
    const handleChangeAndCloseCombobox = useCallback((e)=>{
        handleChange(e);
        // Giving the button focus hides the select dropdown
        chipButtonRef?.current?.focus();
    }, [
        handleChange
    ]);
    const handleChipClick = useCallback(()=>{
        // This click event will trigger display of the combobox - until it is displayed its children
        // cannot receive focus. Wait briefly and then hand focus to the input.
        setTimeout(()=>{
            comboboxInputRef.current && comboboxInputRef.current.focus();
        }, 100);
    }, [
        comboboxInputRef
    ]);
    return React__default.createElement(Stack, {
        "data-hpy-web-combobox-field": true,
        gap: 0.25,
        ...setErrorDataAttribute(hasError(meta))
    }, React__default.createElement(StyledListboxInput, {
        "data-hpy-web-combobox-field-input": true,
        ...setTestId("ChipComboboxField-" + testId),
        ...props,
        value: field.value,
        portal: true
    }, React__default.createElement(ListboxButton, {
        ref: chipButtonRef,
        as: Chip,
        testId: "ChipComboboxField-" + testId,
        icon: icon,
        label: label,
        sublabel: selectedLabel || placeholder,
        readOnly: props.readOnly,
        isHighlighted: isHighlighted,
        onClick: handleChipClick
    }, buttonChildren), React__default.createElement(SelectPopover, {
        portal: !isModal
    }, React__default.createElement(ListboxList, null, React__default.createElement(StyledCombobox, {
        openOnFocus: true,
        onSelect: handleChangeAndCloseCombobox
    }, React__default.createElement(StyledComboboxInput, {
        ...setTestId("ChipComboboxField-ComboboxInput-" + testId),
        ref: comboboxInputRef,
        value: searchTerm,
        selectOnClick: true,
        autocomplete: false,
        onChange: handleSearchTermChange,
        onBlur: handleBlur,
        // onKeyDown event listener has been used by ListBox to listening "space" which prevent inserting space,
        // rewrite it to make is work as we need.
        onKeyDown: (event)=>{
            if (event.key === " ") {
                setSearchTerm(searchTerm + " ");
            }
        }
    }), React__default.createElement(DEPRECATED_ComboboxList, {
        totalOptions: searchTotalCount
    }, filteredChildren))))), reserveMessageSpace && React__default.createElement(FieldErrorMessage, {
        name: name,
        reserveMessageSpace: reserveMessageSpace
    }));
};
DEPRECATED_ChipComboboxField.dataId = "data-hpy-web-combobox-field";
DEPRECATED_ChipComboboxField.displayName = "ChipComboboxField";

export { DEPRECATED_ChipComboboxField };
